import React, { useState } from "react";
import AppShell from "@/components/AppShell";
import { Bell, Check, ArrowRight } from "@phosphor-icons/react";
import { api } from "@/lib/api";
import { toast } from "sonner";

/**
 * ComingSoonShell — wrapper riusabile per pagine "In arrivo · post-beta".
 *
 * Pattern visivo: badge ambra → titolo editoriale → preview mock → spiegazione
 * onesta del perché non è attivo → 3 bullet "cosa farà" → CTA "Avvisami".
 *
 * Quando l'agente clicca "Avvisami", apriamo automaticamente un'idea
 * nel sistema /api/ideas — più voti = più priorità di rollout.
 */
export default function ComingSoonShell({
  testid,
  badge = "In arrivo · prossimi mesi",
  title,
  subtitle,
  intro,
  preview,         // node — il mock visuale
  whyNotYet,       // string — perché non è attivo
  whatItWillDo,    // [{ icon, label, body }, ...]
  interestSlug,    // string — slug per la richiesta interesse (e per l'idea)
  interestTitle,   // string — titolo idea creata se utente clicca
}) {
  const [submitted, setSubmitted] = useState(false);
  const [busy, setBusy] = useState(false);
  const [why, setWhy] = useState("");

  const subscribe = async () => {
    if (busy) return;
    setBusy(true);
    try {
      const reasonBlock = why.trim() ? `\n\nPerché lo vorrei: ${why.trim()}` : "";
      await api.post("/ideas", {
        title: interestTitle,
        body: `Sono interessato a "${interestTitle}". Avvisatemi quando è disponibile.${reasonBlock}`,
        category: "growth",
      }).catch(() => null);
      setSubmitted(true);
      toast.success("Sei in lista", {
        description: why.trim()
          ? "Grazie per averci spiegato il perché — i casi d'uso reali ci aiutano a costruirla meglio."
          : "Ti avvisiamo all'attivazione.",
        duration: 5000,
      });
    } catch (_) {
      toast.error("Riprova tra qualche secondo");
    } finally {
      setBusy(false);
    }
  };

  return (
    <AppShell title={title} breadcrumb={title}>
      <div className="max-w-3xl mx-auto" data-testid={testid}>
        {/* Hero */}
        <header className="mb-8">
          <div className="inline-flex items-center gap-2 px-2.5 py-1 rounded-full bg-amber-100 border border-amber-200 mb-3">
            <Bell size={11} weight="fill" className="text-amber-700" />
            <span className="text-[10.5px] uppercase tracking-[0.18em] font-bold text-amber-800">
              {badge}
            </span>
          </div>
          <h1 className="text-[28px] sm:text-[34px] font-semibold text-[#1C1C1A] tracking-tight leading-tight">
            {title}
            {subtitle && <> <span className="font-editorial italic text-[#3A3A36]">{subtitle}</span></>}
          </h1>
          <p className="mt-3 text-[14.5px] text-[#3A3A36] leading-relaxed max-w-xl">
            {intro}
          </p>
        </header>

        {/* Preview mock */}
        {preview && (
          <section className="bg-white border border-[#EAE7DF] rounded-2xl p-5 mb-6 relative overflow-hidden">
            <div className="absolute top-3 right-3 text-[9px] uppercase tracking-[0.18em] font-bold bg-[#1C1C1A] text-white px-2 py-1 rounded">
              Anteprima
            </div>
            {preview}
          </section>
        )}

        {/* Why not yet */}
        {whyNotYet && (
          <section className="bg-[#F1F4ED] border border-[#7C8B6E]/30 rounded-xl p-5 mb-6">
            <div className="text-[10.5px] uppercase tracking-[0.18em] text-[#7C8B6E] font-bold mb-1.5">
              Perché non è ancora attivo
            </div>
            <p className="text-[13.5px] text-[#1C1C1A] leading-relaxed">
              {whyNotYet}
            </p>
          </section>
        )}

        {/* What it will do */}
        {whatItWillDo?.length > 0 && (
          <section className="mb-6">
            <h2 className="text-[16px] font-semibold text-[#1C1C1A] mb-3 tracking-tight">
              Cosa farà quando sarà attivo
            </h2>
            <ul className="space-y-2.5">
              {whatItWillDo.map(({ icon: Icon, label, body }) => (
                <li key={label} className="flex items-start gap-3 bg-white border border-[#EAE7DF] rounded-xl p-3.5">
                  <div className="w-8 h-8 rounded-lg bg-[#F1F4ED] flex items-center justify-center shrink-0">
                    <Icon size={14} weight="duotone" className="text-[#7C8B6E]" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-[13.5px] font-semibold text-[#1C1C1A]">{label}</div>
                    <div className="text-[12px] text-[#6B6A65] mt-0.5 leading-relaxed">{body}</div>
                  </div>
                </li>
              ))}
            </ul>
          </section>
        )}

        {/* CTA */}
        <section className="bg-[#1C1C1A] rounded-2xl p-5 sm:p-6 text-white">
          <div className="text-[10.5px] uppercase tracking-[0.18em] text-[#7C8B6E] font-bold mb-2">
            Avvisami al rilascio
          </div>
          <h3 className="text-[20px] font-semibold tracking-tight">
            Lo vorresti subito?
          </h3>
          <p className="text-[13.5px] text-white/70 mt-2 leading-relaxed">
            Più agenti lo chiedono, prima lo attiviamo. È così che decidiamo
            l'ordine delle prossime feature: roadmap pubblica, voti dagli
            agenti, niente sorprese.
          </p>
          {submitted ? (
            <div className="mt-4 inline-flex items-center gap-2 px-3.5 py-2 bg-[#7C8B6E]/20 border border-[#7C8B6E]/50 rounded-lg text-[13px] font-medium" data-testid={`${testid || interestSlug}-subscribed`}>
              <Check size={14} weight="bold" className="text-[#7C8B6E]" />
              Sei in lista — ti scriviamo all'attivazione.
            </div>
          ) : (
            <>
              <div className="mt-4">
                <label
                  htmlFor={`${interestSlug}-why`}
                  className="block text-[11px] uppercase tracking-[0.14em] text-white/60 font-bold mb-1.5"
                >
                  Perché lo vorresti? <span className="font-normal lowercase tracking-normal text-white/40">(facoltativo)</span>
                </label>
                <textarea
                  id={`${interestSlug}-why`}
                  value={why}
                  onChange={(e) => setWhy(e.target.value)}
                  rows={2}
                  maxLength={400}
                  placeholder="Es: 'mi serve per richiamare i clienti dopo la visita anche di sera'"
                  className="w-full bg-white/8 border border-white/15 rounded-lg px-3 py-2 text-[13px] text-white placeholder-white/30 focus:outline-none focus:border-[#7C8B6E] focus:bg-white/12 resize-none"
                  data-testid={`${interestSlug}-why-input`}
                />
                <div className="mt-1 text-[10.5px] text-white/40 text-right">
                  {why.length}/400
                </div>
              </div>
              <button
                onClick={subscribe}
                disabled={busy}
                data-testid={`${testid || interestSlug}-notify-btn`}
                className="mt-1 inline-flex items-center gap-2 bg-white text-[#1C1C1A] hover:bg-[#F1F4ED] px-4 py-2.5 rounded-lg text-[13.5px] font-semibold disabled:opacity-50"
              >
                {busy ? "Un attimo…" : "Avvisami quando è pronto"}
                {!busy && <ArrowRight size={13} weight="bold" />}
              </button>
            </>
          )}
        </section>
      </div>
    </AppShell>
  );
}
